// Belt parts as structures (§31 S2, design "Conveyor Belt Part").
//
// A part is a **1-HP non-obstruction structure** (`BELT_ROW`): built at once
// for three cobble beside one of your pieces (S3's rule), plowed, crushed and
// broken by movegen through the position's structure mirror exactly as a
// turret is. It lives in the industry's belt maps — direction, owner, switch,
// cargo — and joins the mirror (`Position.structures`/`structureHealth` plus
// `Position.belts`) through `syncBeltMirror`, run after every command.

import { type Color, pieceColor, type Side } from "../board";
import { STONE } from "../materials";
import {
  areEnemies,
  beltPartAt,
  hasCell,
  invalidateMoves,
  pieceAt,
  type Position
} from "../position";
import { BELT_ROW, type Grade, RULES } from "../rules";
import {
  addHeap,
  beltOwnerAt,
  bufferAtLeast,
  dropOnGround,
  hasBelt,
  heapAt,
  type IndustryState,
  makeUnit,
  structureAt,
  takeUnitOf,
  unitMeets
} from "./state";
import { removeBelt } from "./belts";
import {
  groundFunds,
  liftsAdjacency,
  neighbours,
  storesNearest,
  takeGroundNear,
  takeStockOf
} from "./structures";

/** What one part costs: `RULES.beltCost` cobble, any stone rung at or above. */
export const BELT_BILL = { line: STONE, grade: RULES.beltGrade, count: RULES.beltCost } as const;

export type BeltPlacementError =
  | "off-board"
  | "occupied"
  | "on-heap"
  | "belt-exists"
  | "not-beside-you"
  | "too-poor";

/** `owner`'s pieces touching `sq`, in square order — who pays for a part there. */
export function beltBuilders(position: Position, sq: number, owner: Color): number[] {
  return neighbours(sq)
    .filter((at) => {
      const piece = pieceAt(position, at);
      return piece !== 0 && pieceColor(piece) === owner;
    })
    .sort((a, b) => a - b);
}

/**
 * How much of the bill `owner` can pay at `sq`: touching inboxes, then loose
 * ground on and beside the site (§31 Q13), then stores.
 */
export function beltFunds(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): number {
  let total = 0;
  for (const by of beltBuilders(position, sq, owner)) {
    total += bufferAtLeast(industry, by, BELT_BILL.grade as Grade, "in", BELT_BILL.line);
  }
  total += groundFunds(industry, sq, BELT_BILL);
  for (const store of storesNearest(industry, owner, sq)) {
    for (const unit of structureAt(industry, store)!.stock) {
      if (unitMeets(unit, BELT_BILL.line, BELT_BILL.grade)) total++;
    }
  }
  return total;
}

/**
 * Why `owner` cannot lay a part on `sq` now, or null. Bare of pieces, heaps,
 * buildings and belts; beside a piece of theirs (unless `liftsAdjacency`);
 * and three cobble within reach. Territory no longer enters into it (S2).
 */
export function beltPlacementError(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): BeltPlacementError | null {
  if (!hasCell(position, sq)) return "off-board";
  if (hasBelt(industry, sq)) return "belt-exists";
  // Nothing is built under a piece, anybody's, nor on another building.
  if (pieceAt(position, sq) !== 0) return "occupied";
  if (structureAt(industry, sq)) return "occupied";
  if (heapAt(industry, sq) > 0) return "on-heap";
  if (!liftsAdjacency(industry, owner) && beltBuilders(position, sq, owner).length === 0) {
    return "not-beside-you";
  }
  if (beltFunds(position, industry, sq, owner) < BELT_BILL.count) return "too-poor";
  return null;
}

/**
 * Spend one part's bill, in `beltFunds`' order, cheapest unit first. The
 * caller has checked `beltPlacementError`.
 */
export function payForBelt(
  position: Position,
  industry: IndustryState,
  sq: number,
  owner: Color
): void {
  const builders = beltBuilders(position, sq, owner);
  for (let i = 0; i < BELT_BILL.count; i++) {
    let paid = false;
    for (const by of builders) {
      if (takeUnitOf(industry, by, BELT_BILL.line, BELT_BILL.grade) >= 0) {
        paid = true;
        break;
      }
    }
    if (paid) continue;
    if (takeGroundNear(industry, sq, BELT_BILL)) continue;
    for (const store of storesNearest(industry, owner, sq)) {
      if (takeStockOf(structureAt(industry, store)!, BELT_BILL.line, BELT_BILL.grade)) break;
    }
  }
}

/**
 * A part destroyed by the board — plowed, crushed, broken under a shoved
 * piece, or its army surrendering: the cargo spills as scrap on the square
 * (grade dies with its carrier) and the fabric drops as cobble, S1's wreck.
 * Clears the mirror too, so the industry may wreck one with no move made.
 */
export function wreckBelt(position: Position, industry: IndustryState, sq: number): void {
  if (!hasBelt(industry, sq)) return;
  const cargo = removeBelt(industry, sq).length;
  if (cargo > 0) addHeap(industry, sq, cargo);
  for (let i = 0; i < BELT_BILL.count; i++) dropOnGround(industry, sq, makeUnit(0, 0, STONE));
  if (position.structures.has(sq) && beltPartAt(position, sq)) {
    invalidateMoves(position);
    position.structures.delete(sq);
    position.structureHealth.delete(sq);
    position.belts.delete(sq);
  }
}

/**
 * An **enemy** of `mover` arriving on a part breaks it — the doors that put a
 * piece on a square without a move (a re-aim step, a lance step). A move's
 * own breakage is `Move.broken`.
 */
export function crushBeltUnder(
  position: Position,
  industry: IndustryState,
  sq: number,
  mover: Side
): void {
  if (!hasBelt(industry, sq)) return;
  if (!areEnemies(position, beltOwnerAt(industry, sq), mover)) return;
  wreckBelt(position, industry, sq);
}

/**
 * Bring the position's belt mirror into line with the industry's belt maps.
 * Answers whether anything changed. Writes `position` in place — the caller
 * clones first when it may be shared.
 */
export function beltMirrorStale(position: Position, industry: IndustryState): boolean {
  // No board yet (the empty state `new-game` is applied to).
  if (position === undefined || industry === undefined) return false;
  // A position decoded from before the field existed.
  if (position.belts === undefined) return true;
  if (position.belts.size === 0 && industry.beltOwner.size === 0) return false;
  for (const sq of position.belts) {
    if (!hasBelt(industry, sq)) return true;
    if (position.structures.get(sq) !== beltOwnerAt(industry, sq)) return true;
  }
  for (const sq of industry.beltOwner.keys()) {
    if (!position.belts.has(sq)) return true;
  }
  return false;
}

export function syncBeltMirror(position: Position, industry: IndustryState): void {
  invalidateMoves(position);
  position.belts ??= new Set();
  for (const sq of [...position.belts]) {
    if (hasBelt(industry, sq)) continue;
    position.belts.delete(sq);
    position.structures.delete(sq);
    position.structureHealth.delete(sq);
  }
  for (const [sq, owner] of industry.beltOwner) {
    position.belts.add(sq);
    position.structures.set(sq, owner);
    position.structureHealth.set(sq, BELT_ROW.health);
  }
}
